import { SearchableKeyword } from '../../../../../../ui/gzSearchableAccordionsPanel/types';
import {
  GeneralAdvancedSearchableKeywords,
  GeneralCommunicationSearchableKeywords,
  GeneralDetailsSearchableKeywords,
  GeneralInheritanceRulesSearchableKeywords,
  GeneralNotificationsFormValue,
  GeneralNotificationsSearchableKeywords,
  GeneralSecurityTelemetrySearchableKeywords,
  GeneralUpdateFormValue,
  GeneralUpdateSearchableKeywords,
} from './general.types';
import { translateFn } from '../../../../../../utils/translator.helper';
import {
  CommunicationProxyType,
  FORMAT_TYPE_JSON,
  GeneralDisplaySilentModeIssuesVisibilityAlertAction,
  GeneralUpdateRing,
  GeneralUpdateSettingsSchedulerRebootDay,
  PasswordConfigProfile,
  SecurityTelemetryProtocol,
  SecurityTelemetryProxyType,
  SecurityTelemetrySiemType,
  WeekDays,
} from '@nirvana/shared/dtos/types/policies/common';
import { SelectOption } from '../../../../../../ui/gzDropdown/types';
import { htmlEncode } from '../../../../../../utils/escape.utils';
import { WeekDaysKeys } from '../../../../../common/policyPanel/uiSettings/schedulerTemplate/types';
import { ListItem } from '../../../../../../ui/gzSingleSelectList/types';
import { Feature } from '@nirvana/shared/constants';

export function getSiemSolutionsTypes() {
  return [
    {
      title: translateFn('Splunk (HTTP)'),
      value: SecurityTelemetrySiemType.SPLUNK,
    },
    {
      title: translateFn('Syslog (JSON)'),
      value: SecurityTelemetrySiemType.SYSLOG,
    },
  ];
}

export function getGeneralSecurityTelemetrySearchableKeywords(): {
  [type in GeneralSecurityTelemetrySearchableKeywords]: SearchableKeyword;
} {
  return {
    [GeneralSecurityTelemetrySearchableKeywords.SecurityTelemetry]: {
      label: 'Security Telemetry',
      translatedLabel: translateFn('Security Telemetry'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.SiemConnectionSettings]: {
      label: 'SIEM connection settings',
      translatedLabel: translateFn('SIEM connection settings'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.SiemSolution]: {
      label: 'SIEM solution',
      translatedLabel: translateFn('SIEM solution'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.ServerUrl]: {
      label: 'Server URL',
      translatedLabel: translateFn('Server URL'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.Token]: {
      label: 'Token',
      translatedLabel: translateFn('Token'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.HostnameIp]: {
      label: 'Hostname/IP',
      translatedLabel: translateFn('Hostname/IP'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.Port]: {
      label: 'Port',
      translatedLabel: translateFn('Port'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.IgnoreSslErrors]: {
      label: 'Ignore SSL errors',
      translatedLabel: translateFn('Ignore SSL errors'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.EventTypes]: {
      label: 'Event types',
      translatedLabel: translateFn('Event types'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.ProcessCreate]: {
      label: 'Process create',
      translatedLabel: translateFn('Process create'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.TerminateProcess]: {
      label: 'Terminate process',
      translatedLabel: translateFn('Terminate process'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.NetworkConnection]: {
      label: 'Network Connection',
      translatedLabel: translateFn('Network Connection'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.Logon]: {
      label: 'Logon',
      translatedLabel: translateFn('Logon'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.Logoff]: {
      label: 'Logoff',
      translatedLabel: translateFn('Logoff'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.CreatFile]: {
      label: 'Create file',
      translatedLabel: translateFn('Create file'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.DeleteFile]: {
      label: 'Delete file',
      translatedLabel: translateFn('Delete file'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.ModifyFile]: {
      label: 'Modify file',
      translatedLabel: translateFn('Modify file'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.ReadFromFile]: {
      label: 'Read from file',
      translatedLabel: translateFn('Read from file'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.MoveFile]: {
      label: 'Move file',
      translatedLabel: translateFn('Move file'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.RegistryCreateKey]: {
      label: 'Registry create key',
      translatedLabel: translateFn('Registry create key'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.RegistryDeleteKey]: {
      label: 'Registry delete key',
      translatedLabel: translateFn('Registry delete key'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.RegistryModifyValue]: {
      label: 'Registry modify value',
      translatedLabel: translateFn('Registry modify value'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.RegistryDeleteValue]: {
      label: 'Registry delete value',
      translatedLabel: translateFn('Registry delete value'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.CommunicationBetweenEndpointsSIEM]: {
      label: 'Communication between endpoints and SIEMs',
      translatedLabel: translateFn('Communication between endpoints and SIEMs'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.ProxyConfigured]: {
      label: 'Use the proxy defined in Agent > Settings',
      translatedLabel: translateFn('Use the proxy defined in Agent > Settings'),
    },
    [GeneralSecurityTelemetrySearchableKeywords.DoNotUseProxy]: {
      label: 'Do not use a proxy',
      translatedLabel: translateFn('Do not use a proxy'),
    },
  };
}
export function getGeneralCommunicationSearchableKeywords() {
  return {
    [GeneralCommunicationSearchableKeywords.Communication]: {
      label: 'Communication',
      translatedLabel: translateFn('Communication'),
    },
    [GeneralCommunicationSearchableKeywords.EndpointCommunicationAssignment]: {
      label: 'Endpoint communication assignment',
      translatedLabel: translateFn('Endpoint communication assignment'),
    },
    [GeneralCommunicationSearchableKeywords.CommunicationBetweenEndpointsAndRelays]: {
      label: 'Communication between endpoints and Relays or GravityZone',
      translatedLabel: translateFn('Communication between endpoints and Relays or GravityZone'),
    },
    [GeneralCommunicationSearchableKeywords.UsePreviousSettings]: {
      label: 'Use previous settings',
      translatedLabel: translateFn('Use previous settings'),
    },
    [GeneralCommunicationSearchableKeywords.UseProxyDefinedInSettings]: {
      label: 'Use the proxy defined in Agent > Settings',
      translatedLabel: translateFn('Use the proxy defined in Agent > Settings'),
    },
    [GeneralCommunicationSearchableKeywords.DoNotUseProxy]: {
      label: 'Do not use proxy',
      translatedLabel: translateFn('Do not use proxy'),
    },
    [GeneralCommunicationSearchableKeywords.CommunicationBetweenEndpointsAndCloudServices]: {
      label: 'Communication between endpoints and Cloud Services',
      translatedLabel: translateFn('Communication between endpoints and Cloud Services'),
    },
    [GeneralCommunicationSearchableKeywords.AutoDetectProxySettings]: {
      label: 'Autodetect proxy settings',
      translatedLabel: translateFn('Autodetect proxy settings'),
    },
    [GeneralCommunicationSearchableKeywords.RemoteShellConnectionWithEndpoint]: {
      label: 'Remote Shell connection with endpoint',
      translatedLabel: translateFn('Remote Shell connection with endpoint'),
    },
  };
}

export function getGeneralDetailsSearchableKeywords() {
  return {
    [GeneralDetailsSearchableKeywords.PolicyDetails]: {
      label: 'Policy details',
      translatedLabel: translateFn('Policy details'),
    },
    [GeneralDetailsSearchableKeywords.Name]: {
      label: 'Name',
      translatedLabel: translateFn('Name'),
    },
    [GeneralDetailsSearchableKeywords.AllowChangeByOtherUsers]: {
      label: 'Allow other users to change this policy',
      translatedLabel: translateFn('Allow other users to change this policy'),
    },
    [GeneralDetailsSearchableKeywords.History]: {
      label: 'History',
      translatedLabel: translateFn('History'),
    },
    [GeneralDetailsSearchableKeywords.CreatedBy]: {
      label: 'Created by',
      translatedLabel: translateFn('Created by'),
    },
    [GeneralDetailsSearchableKeywords.CreatedOn]: {
      label: 'Created on',
      translatedLabel: translateFn('Created on'),
    },
    [GeneralDetailsSearchableKeywords.ModifiedOn]: {
      label: 'Modified on',
      translatedLabel: translateFn('Modified on'),
    },
    [GeneralDetailsSearchableKeywords.TechnicalSupportInformation]: {
      label: 'Technical Support information',
      translatedLabel: translateFn('Technical Support information'),
    },
    [GeneralDetailsSearchableKeywords.Website]: {
      label: 'Website',
      translatedLabel: translateFn('Website'),
    },
    [GeneralDetailsSearchableKeywords.Email]: {
      label: 'Email',
      translatedLabel: translateFn('Email'),
    },
    [GeneralDetailsSearchableKeywords.Phone]: {
      label: 'Phone',
      translatedLabel: translateFn('Phone'),
    },
  };
}

export function getGeneralAdvancedSearchableKeywords() {
  return {
    [GeneralAdvancedSearchableKeywords.Settings]: {
      label: 'Settings',
      translatedLabel: translateFn('Settings'),
    },
    [GeneralAdvancedSearchableKeywords.UninstallPasswordConfiguration]: {
      label: 'Uninstall password configuration',
      translatedLabel: translateFn('Uninstall password configuration'),
    },
    [GeneralAdvancedSearchableKeywords.KeepInstallationSettings]: {
      label: 'Keep installation settings',
      translatedLabel: translateFn('Keep installation settings'),
    },
    [GeneralAdvancedSearchableKeywords.SetUninstallPassword]: {
      label: 'Set uninstall password',
      translatedLabel: translateFn('Set uninstall password'),
    },
    [GeneralAdvancedSearchableKeywords.Password]: {
      label: 'Password',
      translatedLabel: translateFn('Password'),
    },
    [GeneralAdvancedSearchableKeywords.RetypePassword]: {
      label: 'Retype password',
      translatedLabel: translateFn('Retype password'),
    },
    [GeneralAdvancedSearchableKeywords.DisablePassword]: {
      label: 'Disable password',
      translatedLabel: translateFn('Disable password'),
    },
    [GeneralAdvancedSearchableKeywords.ProxyConfiguration]: {
      label: 'Proxy configuration',
      translatedLabel: translateFn('Proxy configuration'),
    },
    [GeneralAdvancedSearchableKeywords.Server]: {
      label: 'Server',
      translatedLabel: translateFn('Server'),
    },
    [GeneralAdvancedSearchableKeywords.Port]: {
      label: 'Port',
      translatedLabel: translateFn('Port'),
    },
    [GeneralAdvancedSearchableKeywords.Username]: {
      label: 'Username',
      translatedLabel: translateFn('Username'),
    },
    [GeneralAdvancedSearchableKeywords.PowerUser]: {
      label: 'Power user',
      translatedLabel: translateFn('Power user'),
    },
    [GeneralAdvancedSearchableKeywords.Options]: {
      label: 'Options',
      translatedLabel: translateFn('Options'),
    },
    [GeneralAdvancedSearchableKeywords.RemoveEventsOlderThan]: {
      label: 'Remove events older than (days)',
      translatedLabel: translateFn('Remove events older than (days)'),
    },
    [GeneralAdvancedSearchableKeywords.SubmitCrashReportsToBitdefender]: {
      label: 'Submit crash reports to Bitdefender',
      translatedLabel: translateFn('Submit crash reports to Bitdefender'),
    },
    [GeneralAdvancedSearchableKeywords.SubmitSuspiciousFilesForAnalysis]: {
      label: 'Submit suspicious files for analysis',
      translatedLabel: translateFn('Submit suspicious files for analysis'),
    },
    [GeneralAdvancedSearchableKeywords.SendFeedbackRegardingSecurityAgentsHealth]: {
      label: "Send feedback regarding security agents' health",
      translatedLabel: translateFn("Send feedback regarding security agents' health"),
    },
    [GeneralAdvancedSearchableKeywords.UseBitdefenderGlobalProtectiveNetwork]: {
      label: 'Use Bitdefender Global Protective Network to enhance protection',
      translatedLabel: translateFn('Use Bitdefender Global Protective Network to enhance protection'),
    },
    [GeneralAdvancedSearchableKeywords.AllowEndpointToSendUserLoginDataToGZ]: {
      label: 'Allow endpoints to send user login data to GravityZone',
      translatedLabel: translateFn('Allow endpoints to send user login data to GravityZone'),
    },
  };
}

export function getGeneralSecurityTelemetryDefaultValue() {
  return {
    enabled: false,
    siem: SecurityTelemetrySiemType.SPLUNK,
    protocol: SecurityTelemetryProtocol.HTTP,
    format: FORMAT_TYPE_JSON,
    allowSelfSignedCertificate: false,
    config: {
      url: '',
      key: '',
    },
    eventTypes: {
      createFile: false,
      createProcess: false,
      deleteFile: false,
      logon: false,
      logout: false,
      modifyFile: false,
      moveFile: false,
      networkConnection: false,
      readFromFile: false,
      regCreateKey: false,
      regDeleteKey: false,
      regDeleteValue: false,
      regModifyValue: false,
      terminateProcess: false,
    },
    proxy: {
      profile: SecurityTelemetryProxyType.ENABLE,
    },
  };
}

export function getGeneralCommunicationDefaultValue() {
  return {
    cloudServicesProxy: {
      profile: CommunicationProxyType.KEEP_CURRENT_SETTINGS,
    },
    ecsAssignments: [],
    ecsProxy: {
      profile: CommunicationProxyType.KEEP_CURRENT_SETTINGS,
    },
    remoteShell: {
      enabled: false,
    },
  };
}

export function getGeneralDetailsDefaultValue() {
  return {
    name: '',
    lastModified: '',
    ownerFullName: '',
    created: '',
    uiSettings: {
      general: {
        allowChangeByOtherUsers: false,
        display: {
          supportInformation: {
            website: '',
            email: '',
            phone: '',
          },
        },
      },
    },
  };
}

export function getGeneralAdvancedDefaultValue() {
  return {
    uiSettings: {
      general: {
        advanced: {
          passwordConfig: {
            profile: PasswordConfigProfile.KEEP_CURRENT_SETTINGS,
            value: '',
          },
          powerUser: {
            enabled: false,
            password: '',
          },
          settings: {
            removeEvents: {
              settings: {
                days: 30,
              },
            },
            reportActiveSessions: true,
            sendTelemetry: true,
            submitReports: true,
            submitSuspicious: true,
            useGlobalProtectiveNetwork: true,
          },
        },
        update: {
          settings: {
            proxy: {
              enable: false,
              profile: 1,
              settings: {
                server: 'http://proxy',
                port: 12,
                username: 'username',
                password: '',
              },
            },
          },
        },
      },
    },
  };
}

export const GENERAL_ADVANCED_SECTION_DEFAULT_VALUE = getGeneralAdvancedDefaultValue().uiSettings.general.advanced;

// Notifications section
export const MIN_ISSUE_VISIBILITY_SHOW_AFTER = 1;
export const MAX_ISSUE_VISIBILITY_SHOW_AFTER = 365;

export function isSandboxVisibleForGeneralNotificationsShowPopupsSettings() {
  return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SANDBOX_ANALYZER_VISIBLE]);
}
export function isApplicationControlVisibleForGeneralNotificationsShowPopupsSettings() {
  return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_APPLICATION_CONTROL]);
}
export function isExchangeVisibleForGeneralNotificationsShowPopupsSettings() {
  return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EXCHANGE]);
}
export function isEncryptionVisibleForGeneralNotificationsShowPopupsSettings() {
  return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_VOLUME_ENCRYPTION_VISIBLE]);
}

export function getGeneralNotificationsDefaultValue(): GeneralNotificationsFormValue {
  return {
    uiSettings: {
      general: {
        display: {
          silentMode: {
            enable: false,
            iconNotificationArea: true,
            showPopups: {
              enable: false,
              settings: {
                general: {
                  info: false,
                  low: false,
                  critical: false,
                },
                update: {
                  info: false,
                  low: false,
                  critical: false,
                },
                antimalware: {
                  info: false,
                  low: false,
                  critical: false,
                },
                ...(isSandboxVisibleForGeneralNotificationsShowPopupsSettings()
                  ? {
                      sandboxAnalyzer: {
                        info: false,
                        low: false,
                        critical: false,
                      },
                    }
                  : {}),
                firewall: {
                  info: false,
                  low: false,
                  critical: false,
                },
                ...(isApplicationControlVisibleForGeneralNotificationsShowPopupsSettings()
                  ? {
                      applicationControl: {
                        info: false,
                        low: false,
                        critical: false,
                      },
                    }
                  : {}),
                contentControl: {
                  info: false,
                  low: false,
                  critical: false,
                },
                deviceControl: {
                  info: false,
                  low: false,
                  critical: false,
                },
                ...(isExchangeVisibleForGeneralNotificationsShowPopupsSettings()
                  ? {
                      exchange: {
                        info: false,
                        low: false,
                        critical: false,
                      },
                    }
                  : {}),
                ...(isEncryptionVisibleForGeneralNotificationsShowPopupsSettings()
                  ? {
                      encryption: {
                        info: false,
                        low: false,
                        critical: false,
                      },
                    }
                  : {}),
              },
            },
            displayAlertsPopups: false,
            issuesVisibility: {
              enable: true,
              profile: 1,
              general: true,
              antimalware: true,
              firewall: true,
              contentControl: false,
              update: true,
              installationRestartNotifications: 2,
              onAccessNotifications: 3,
              onDemandNotifications: 2,
              dissinfectionRestartNotification: 3,
              updateRestartNotifications: 3,
              showAfter: 7,
              cloudConnectionNotifications: 3,
            },
            endpointRestartPopup: {
              enable: false,
              update: false,
              patchManagement: false,
            },
          },
        },
      },
    },
  };
}

// Update section
export const MIN_UPDATE_INTERVAL = 1;
export const MAX_UPDATE_INTERVAL = 24;
export const GENERAL_UPDATE_DEFAULT_VALUE: GeneralUpdateFormValue = {
  uiSettings: {
    general: {
      update: {
        settings: {
          productUpdateScheduler: {
            enabled: true,
            occurrence: 1,
            updateInterval: 1,
            weekDays: {
              days: [],
              startHour: 0,
              startMinute: 0,
              endHour: 23,
              endMinute: 59,
            },
            postponeReboot: true,
            rebootAfterInstalling: {
              enable: false,
              settings: {
                day: 7,
                hour: 21,
                minutes: 0,
              },
            },
          },
          signatureUpdateScheduler: {
            enabled: true,
            occurrence: 1,
            updateInterval: 1,
            weekDays: {
              days: [],
              startHour: 0,
              startMinute: 0,
              endHour: 23,
              endMinute: 59,
            },
          },
          updateRing: 0,
          proxy: {
            enable: false,
            profile: 1,
            settings: {
              server: 'http://proxy',
              port: 12,
              username: 'username',
              password: '',
            },
          },
          defaultLocation: true,
        },
        updateLocations: [],
      },
    },
  },
};

export function getWeekDaysFormOptions(): SelectOption[] {
  // we should use getSchedulerStrings method from BaseWebComponentTranslator when add/edit policy will be full web components
  const shortNames = {
    Sunday: htmlEncode(translateFn('Sun')),
    Monday: htmlEncode(translateFn('Mon')),
    Tuesday: htmlEncode(translateFn('Tue')),
    Wednesday: htmlEncode(translateFn('Wed')),
    Thursday: htmlEncode(translateFn('Thu')),
    Friday: htmlEncode(translateFn('Fri')),
    Saturday: htmlEncode(translateFn('Sat')),
  };

  return Array.from(WeekDaysKeys, (element: string) => ({
    title: shortNames[element],
    value: parseInt(WeekDays[element], 10),
  }));
}
export function getGeneralUpdateRingFormOptions(): ListItem[] {
  const filterOptions: ListItem[] = [];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE])) {
    filterOptions.push({
      parent: translateFn('Staging rings'),
      label: translateFn('Production ring'),
      value: GeneralUpdateRing.PRODUCTION_RING,
    });
    filterOptions.push({
      parent: translateFn('Staging rings'),
      label: translateFn('Test ring 1'),
      value: GeneralUpdateRing.TEST1_RING,
    });
    filterOptions.push({
      parent: translateFn('Staging rings'),
      label: translateFn('Test ring 2'),
      value: GeneralUpdateRing.TEST2_RING,
    });
  }

  if (Application.Main.user.companyInfo.specifics.superFastRingTestCompany) {
    filterOptions.push({
      parent: translateFn('Bitdefender rings'),
      label: translateFn('Superfast ring'),
      value: GeneralUpdateRing.SUPER_FAST_RING,
    });
  }

  return [
    ...filterOptions,
    {
      parent: translateFn('Bitdefender rings'),
      label: translateFn('Fast ring'),
      value: GeneralUpdateRing.FAST_RING,
    },
    {
      parent: translateFn('Bitdefender rings'),
      label: translateFn('Slow ring'),
      value: GeneralUpdateRing.SLOW_RING,
    },
  ];
}
export function getGeneralUpdateSettingsSchedulerRebootDayFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Monday'),
      value: GeneralUpdateSettingsSchedulerRebootDay.MONDAY,
    },
    {
      title: translateFn('Tuesday'),
      value: GeneralUpdateSettingsSchedulerRebootDay.TUESDAY,
    },
    {
      title: translateFn('Wednesday'),
      value: GeneralUpdateSettingsSchedulerRebootDay.WEDNESDAY,
    },
    {
      title: translateFn('Thursday'),
      value: GeneralUpdateSettingsSchedulerRebootDay.THURSDAY,
    },
    {
      title: translateFn('Friday'),
      value: GeneralUpdateSettingsSchedulerRebootDay.FRIDAY,
    },
    {
      title: translateFn('Saturday'),
      value: GeneralUpdateSettingsSchedulerRebootDay.SATURDAY,
    },
    {
      title: translateFn('Sunday'),
      value: GeneralUpdateSettingsSchedulerRebootDay.SUNDAY,
    },
    {
      title: translateFn('Daily'),
      value: GeneralUpdateSettingsSchedulerRebootDay.EVERY_DAY,
    },
  ];
}

export function getGeneralNotificationsSearchableKeywords(): {
  [type in GeneralNotificationsSearchableKeywords]: SearchableKeyword;
} {
  return {
    [GeneralNotificationsSearchableKeywords.Notifications]: {
      translatedLabel: translateFn('Notifications'),
      label: 'Notifications',
    },
    [GeneralNotificationsSearchableKeywords.SilentMode]: {
      translatedLabel: translateFn('Silent Mode'),
      label: 'Silent Mode',
    },
    [GeneralNotificationsSearchableKeywords.ShowIconInNotificationArea]: {
      translatedLabel: translateFn('Show icon in notification area'),
      label: 'Show icon in notification area',
    },
    [GeneralNotificationsSearchableKeywords.DisplayAlertsPopups]: {
      translatedLabel: translateFn('Display alerts pop-ups'),
      label: 'Display alerts pop-ups',
    },
    [GeneralNotificationsSearchableKeywords.DisplayNotificationPopups]: {
      translatedLabel: translateFn('Display notification pop-ups'),
      label: 'Display notification pop-ups',
    },
    [GeneralNotificationsSearchableKeywords.EndpointIssuesVisibility]: {
      translatedLabel: translateFn('Endpoint issues visibility'),
      label: 'Endpoint issues visibility',
    },
    [GeneralNotificationsSearchableKeywords.EndpointRestartNotification]: {
      translatedLabel: translateFn('Endpoint restart notifications'),
      label: 'Endpoint restart notifications',
    },
  };
}

const REMOVE_EVENTS_OLDER_THAN_1 = 1;
const REMOVE_EVENTS_OLDER_THAN_2 = 2;
const REMOVE_EVENTS_OLDER_THAN_3 = 3;
const REMOVE_EVENTS_OLDER_THAN_4 = 4;
const REMOVE_EVENTS_OLDER_THAN_5 = 5;
const REMOVE_EVENTS_OLDER_THAN_6 = 6;
const REMOVE_EVENTS_OLDER_THAN_7 = 7;
const REMOVE_EVENTS_OLDER_THAN_15 = 15;
const REMOVE_EVENTS_OLDER_THAN_30 = 30;

export function getGeneralRemoveEventsOlderThanOptions() {
  return [
    {
      title: REMOVE_EVENTS_OLDER_THAN_1,
      value: REMOVE_EVENTS_OLDER_THAN_1,
    },
    {
      title: REMOVE_EVENTS_OLDER_THAN_2,
      value: REMOVE_EVENTS_OLDER_THAN_2,
    },
    {
      title: REMOVE_EVENTS_OLDER_THAN_3,
      value: REMOVE_EVENTS_OLDER_THAN_3,
    },
    {
      title: REMOVE_EVENTS_OLDER_THAN_4,
      value: REMOVE_EVENTS_OLDER_THAN_4,
    },
    {
      title: REMOVE_EVENTS_OLDER_THAN_5,
      value: REMOVE_EVENTS_OLDER_THAN_5,
    },
    {
      title: REMOVE_EVENTS_OLDER_THAN_6,
      value: REMOVE_EVENTS_OLDER_THAN_6,
    },
    {
      title: REMOVE_EVENTS_OLDER_THAN_7,
      value: REMOVE_EVENTS_OLDER_THAN_7,
    },
    {
      title: REMOVE_EVENTS_OLDER_THAN_15,
      value: REMOVE_EVENTS_OLDER_THAN_15,
    },
    {
      title: REMOVE_EVENTS_OLDER_THAN_30,
      value: REMOVE_EVENTS_OLDER_THAN_30,
    },
  ];
}

export function getGeneralDisplaySilentModeIssuesVisibilityAlertActionFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Do not show'),
      value: GeneralDisplaySilentModeIssuesVisibilityAlertAction.DO_NOT_SHOW,
    },
    {
      title: translateFn('Show as warning'),
      value: GeneralDisplaySilentModeIssuesVisibilityAlertAction.SHOW_AS_WARNING,
    },
    {
      title: translateFn('Show as critical'),
      value: GeneralDisplaySilentModeIssuesVisibilityAlertAction.SHOW_AS_CRITICAL,
    },
  ];
}

export function getGeneralInheritanceRulesSearchableKeywords(): {
  [type in GeneralInheritanceRulesSearchableKeywords]: SearchableKeyword;
} {
  return {
    [GeneralInheritanceRulesSearchableKeywords.InheritanceRules]: {
      translatedLabel: translateFn('Inheritance rules'),
      label: 'Inheritance rules',
    },
    [GeneralInheritanceRulesSearchableKeywords.AddInheritanceRules]: {
      translatedLabel: translateFn('Add inheritance rules'),
      label: 'Add inheritance rules',
    },
  };
}

export function getGeneralUpdateSearchableKeywordsSearchableKeywords(): {
  [type in GeneralUpdateSearchableKeywords]: SearchableKeyword;
} {
  return {
    [GeneralUpdateSearchableKeywords.ProductUpdate]: {
      translatedLabel: translateFn('Product update'),
      label: 'Product update',
    },
    [GeneralUpdateSearchableKeywords.Scheduler]: {
      translatedLabel: translateFn('Scheduler'),
      label: 'Scheduler',
    },
    [GeneralUpdateSearchableKeywords.Recurrence]: {
      translatedLabel: translateFn('Recurrence'),
      label: 'Recurrence',
    },
    [GeneralUpdateSearchableKeywords.PostponeReboot]: {
      translatedLabel: translateFn('Postpone reboot'),
      label: 'Postpone reboot',
    },
    [GeneralUpdateSearchableKeywords.Reboot]: {
      translatedLabel: translateFn('Reboot time (if needed)'),
      label: 'Reboot time (if needed)',
    },
    [GeneralUpdateSearchableKeywords.SecurityUpdate]: {
      translatedLabel: translateFn('Security content update'),
      label: 'Security content update',
    },
    [GeneralUpdateSearchableKeywords.UpdateLocations]: {
      translatedLabel: translateFn('Update locations'),
      label: 'Update locations',
    },
    [GeneralUpdateSearchableKeywords.UseBitdefender]: {
      translatedLabel: translateFn('Use Bitdefender Public Update Server as fallback'),
      label: 'Use Bitdefender Public Update Server as fallback',
    },
    [GeneralUpdateSearchableKeywords.UpdateRing]: {
      translatedLabel: translateFn('Update ring'),
      label: 'Update ring',
    },
  };
}
